import { GRID_H, GRID_W } from '../../sim/constants.ts';
import type { RoomDef } from '../../sim/types.ts';
import { ROOMS } from './index.ts';

/**
 * The Grove hub — a real playable room, not a menu screen.
 *
 * Replaces the old button-list `GroveMap`: the player walks and casts here
 * exactly like any of the eight rooms, and each of the eight doors sits one
 * tile above a floating landing that is only reachable by casting a fresh
 * block staircase up to it — the same cast-and-climb technique Room 1
 * teaches, practiced again every time the hub is (re)entered, since a door's
 * blocks are ordinary Seedblocks and don't persist across visits any more
 * than any other room's do.
 *
 * One continuous rising staircase carries all eight doors rather than eight
 * separate ones: each step lands one column further right and one row
 * higher than the last (the direction a cast-ahead step always drifts), and
 * a door waits at head height above every landing. That is what keeps eight
 * doors collision-free in a 16-wide room without them stepping on each
 * other's blocks - it also means reaching door 8 means climbing past 1
 * through 7 again on every visit. That repetition is the direct request
 * ("cast blocks to reach it"), not an oversight, but it is worth knowing
 * about if it turns out to feel like busywork once there is more than a
 * door or two cleared.
 *
 * `locked` is computed fresh from `cleared` every time this is built (App
 * rebuilds the hub on every return to it), mirroring the exact "next
 * uncleared room is open, everything after it is locked, everything before
 * it stays open" rule the old `GroveMap` used.
 *
 * Deliberately outside `ROOMS`: like the Warden test room, it carries no
 * eight-room-budget or solvability-harness obligation - there is no single
 * "canonical solution" to a hub, only eight of them.
 */
export const GROVE_HUB_ID = 'grove-hub';

const FLOOR_ROW = GRID_H - 2; // row 10: same floor convention as every other room
const STAIR_START_COL = 2;

function buildMap(): string[] {
  const rows: string[] = [];
  for (let ty = 0; ty < GRID_H; ty++) {
    if (ty === 0 || ty === GRID_H - 1) {
      rows.push('#'.repeat(GRID_W));
    } else if (ty === FLOOR_ROW) {
      rows.push('#'.repeat(GRID_W));
    } else {
      rows.push(`#${'.'.repeat(GRID_W - 2)}#`);
    }
  }
  return rows;
}

export function buildGroveHub(cleared: ReadonlySet<string>): RoomDef {
  const firstUnclearedIndex = ROOMS.findIndex((r) => !cleared.has(r.id));
  const unlockedThrough = firstUnclearedIndex === -1 ? ROOMS.length - 1 : firstUnclearedIndex;

  const doors = ROOMS.map((room, i) => {
    const step = i + 1; // 1-indexed: step i's block lands one column/row past step i-1's
    const doorCol = STAIR_START_COL + step;
    const doorRow = FLOOR_ROW - 1 - step;
    return {
      tx: doorCol,
      ty: doorRow,
      targetRoomId: room.id,
      locked: i > unlockedThrough,
      label: String(step),
    };
  });

  return {
    id: GROVE_HUB_ID,
    name: 'The Grove',
    map: buildMap(),
    spawn: { x: STAIR_START_COL + 0.5, y: FLOOR_ROW },
    // Inert by design: no Moonseed ever exists here, so `hasMoonseed` never
    // becomes true and this can never actually complete the room. Placed at
    // spawn rather than threading an optional `portal` through every type
    // and render call that currently assumes one always exists.
    portal: { tx: STAIR_START_COL, ty: FLOOR_ROW - 1 },
    items: [],
    chimera: [],
    doors,
    // No time pressure in a hub you may need to revisit eight times over.
    moonlight: 1e6,
    // The pre-room "plan before you start" countdown (`useGame`) is for the
    // eight puzzle rooms; a hub you may pass through many times a session
    // doesn't need three frozen seconds every time you walk back into it.
    skipCountdown: true,
  };
}
